import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Shield, Check, AlertTriangle } from "lucide-react";

/**
 * Re-acceptance page for TOS + Privacy Policy when their active version
 * has been bumped beyond what the user has on record.
 *
 * Renders only the docs the user actually needs to sign — anything already
 * accepted at the latest version is skipped.
 */
export default function LegalReaccept() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState(null); // [{type, title, body, signed:false, signature_name:""}, ...]
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: status } = await http.get("/legal/me/status");
        const needed = [];
        for (const t of ["tos", "privacy"]) {
          if (!status?.[t]?.accepted) {
            const { data: doc } = await http.get(`/legal/${t}`);
            needed.push({
              type: t,
              title: doc.title,
              version: doc.version,
              effective_date: doc.effective_date,
              body: doc.body,
              signature_name: user?.name || "",
              signed: false,
            });
          }
        }
        if (!cancelled) {
          if (needed.length === 0) {
            nav(user?.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/parent", { replace: true });
          } else {
            setItems(needed);
          }
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
    return () => { cancelled = true; };
  }, [user, nav]);

  const updateName = (idx, name) => {
    setItems((prev) => prev.map((p, i) => (i === idx ? { ...p, signature_name: name } : p)));
  };

  const allReady = items && items.every((it) => it.signature_name.trim().length >= 3);

  const submit = async () => {
    if (!allReady) return;
    setSubmitting(true);
    try {
      for (const it of items) {
        await http.post(`/legal/${it.type}/accept`, {
          signature_name: it.signature_name.trim(),
        });
      }
      toast.success("Thanks — your acceptance is on record.");
      nav(user?.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/parent", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!items) {
    return <div className="p-12 text-center text-white/40">Loading legal updates…</div>;
  }

  return (
    <div className="min-h-[calc(100vh-64px)] max-w-3xl mx-auto p-6 md:p-10" data-testid="legal-reaccept-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-md bg-electric flex items-center justify-center">
          <Shield size={20} className="text-black" />
        </div>
        <div className="label-mono">VERIFIED · Legal Update</div>
      </div>
      <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight mb-3">
        We updated our <span className="text-electric">legal terms</span>.
      </h1>
      <div className="flex items-start gap-2 p-3 rounded-md bg-amber-400/5 border border-amber-400/25 text-amber-200 mb-6">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <div className="text-xs leading-relaxed">
          <strong className="font-display uppercase tracking-wider">Action required</strong>
          <div className="text-white/70 mt-1">
            Please review and re-accept the updated document{items.length === 1 ? "" : "s"} below to continue using VERIFIED.
            Your previous acceptance is kept on record; this signature applies to the new version going forward.
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {items.map((it, idx) => (
          <div key={it.type} className="bento-card p-4 md:p-6" data-testid={`legal-doc-${it.type}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h2 className="font-display text-xl md:text-2xl font-black uppercase tracking-tight">
                  {it.title}
                </h2>
                <div className="text-[10px] text-white/40 font-mono mt-1">
                  v{it.version} · Effective {it.effective_date}
                </div>
              </div>
              <span className="label-mono text-amber-300">Re-accept</span>
            </div>
            <div
              className="max-h-72 overflow-y-auto p-3 rounded bg-[#0a0a0a] border border-white/8 text-xs text-white/70 leading-relaxed whitespace-pre-wrap font-mono"
              data-testid={`legal-body-${it.type}`}
            >
              {it.body}
            </div>
            <div className="mt-3">
              <label className="label-mono block mb-1.5">Type your full name as your signature</label>
              <input
                value={it.signature_name}
                onChange={(e) => updateName(idx, e.target.value)}
                className="input-dark w-full rounded-md px-3 py-2.5 text-sm"
                placeholder="Full name"
                data-testid={`legal-signature-${it.type}`}
              />
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={submit}
        disabled={!allReady || submitting}
        className="btn-primary w-full justify-center mt-6 disabled:opacity-50"
        data-testid="legal-accept-btn"
      >
        <Check size={14} />
        {submitting ? "Recording acceptance…" : "Accept & Continue"}
      </button>
    </div>
  );
}
